// AccountSession: the app's one handle on sign-in (auth/auth.ts AuthModule underneath). It owns
// when the auth module loads, what the menu shows, and how a refused token is retried.
//
// Loading (free play loads no Privy code):
// - `/api/info.auth` tells which provider the server verifies (`setInfo`, every info answer).
// - The module (auth/load.ts: the fake one, or the lazy Privy island) loads on the first
//   `signIn()` — the "Sign in" button, or a money stake pressed while signed out — or at boot when
//   an earlier visit left the `ballio.auth.v1` marker (a signed-in player coming back: restore the
//   session so the menu says who they are). Signing out clears the marker.
//
// Tokens and retries:
// - `token({fresh})` → the module's access token (null while signed out / not loaded).
// - `authed(call)` runs an authenticated HTTP call with a token; when the server says the token is
//   invalid (401 authInvalid) it asks for a fresh token and tries once more; a second refusal signs
//   out and reports `signedOut` (the app shows "Sign in again"). The money socket does the same
//   for close 4017 inside the Connector (net/discovery.ts).
//
// Username (unique, 3–15 of [A-Za-z0-9_], set once; @ballio/shared account.ts): `setUsername`
// posts it and re-reads the account. `needsUsername` is true while signed in without one; the app
// asks right after the first sign-in and again before a money game.
//
// DOM-free (unit-tested). Consumers: app.ts, ui/menu/account.ts (the account row's view).

import { setUsername as postUsername, type SetUsernameResult } from '../net/account-api.js';
import {
  defaultStorage,
  readKey,
  removeKey,
  type StorageLike,
  writeKey,
} from '../settings/store.js';
import { type ReadonlySignal, signal, type Unsubscribe } from '../ui/dom.js';
import type { AuthInfo, AuthModule, AuthUser } from './auth.js';
import { loadAuth as defaultLoadAuth, type LoadAuthDeps } from './load.js';

/** localStorage key: the provider of the last sign-in ("fake" | "privy"), removed by sign-out. */
export const AUTH_MARKER_KEY = 'ballio.auth.v1';

export type AccountPhase =
  /** The server offers no sign-in (older server, or /api/info not answered yet). */
  | 'unavailable'
  /** Signed out (the module may or may not be loaded). */
  | 'signedOut'
  /** Loading the module / restoring a session / the sign-in modal is open. */
  | 'busy'
  | 'signedIn';

export interface AccountState {
  readonly phase: AccountPhase;
  readonly provider: 'fake' | 'privy' | null;
  /** Signed in only. */
  readonly user: AuthUser | null;
  /** The sign-in module failed to load (Privy blocked or unreachable): the button says so. */
  readonly loadFailed: boolean;
}

export const ACCOUNT_UNAVAILABLE: AccountState = {
  phase: 'unavailable',
  provider: null,
  user: null,
  loadFailed: false,
};

function sameState(a: AccountState, b: AccountState): boolean {
  return (
    a.phase === b.phase &&
    a.provider === b.provider &&
    a.loadFailed === b.loadFailed &&
    a.user?.accountId === b.user?.accountId &&
    a.user?.username === b.user?.username &&
    a.user?.wallet === b.user?.wallet &&
    (a.user?.walletKind ?? null) === (b.user?.walletKind ?? null) &&
    (a.user?.walletProblem ?? null) === (b.user?.walletProblem ?? null) &&
    a.user?.email === b.user?.email &&
    (a.user?.bannedUntil ?? null) === (b.user?.bannedUntil ?? null)
  );
}

/** What `authed` reports besides the call's own result. */
export type AuthedOutcome<T> =
  | { readonly status: 'done'; readonly value: T }
  /** Not signed in (or the module is not loaded). */
  | { readonly status: 'noSession' }
  /** The server refused the token twice: the session was signed out. */
  | { readonly status: 'signedOut' };

export interface AccountSessionDeps {
  readonly baseUrl: string;
  readonly storage?: StorageLike | null;
  /** Default auth/load.ts loadAuth (tests inject a fake module). */
  readonly load?: (info: AuthInfo, deps: LoadAuthDeps) => Promise<AuthModule | null>;
  readonly warn?: (msg: string, err?: unknown) => void;
}

export class AccountSession {
  readonly state: ReadonlySignal<AccountState>;
  private readonly sig = signal<AccountState>(ACCOUNT_UNAVAILABLE, sameState);
  private info: AuthInfo | null = null;
  private module: AuthModule | null = null;
  private loading: Promise<AuthModule | null> | null = null;
  private busyCount = 0;
  private loadFailed = false;
  private offModule: Unsubscribe[] = [];
  private readonly storage: StorageLike | null;
  private readonly load: (info: AuthInfo, deps: LoadAuthDeps) => Promise<AuthModule | null>;
  private readonly deps: AccountSessionDeps;

  constructor(deps: AccountSessionDeps) {
    this.deps = deps;
    this.state = this.sig;
    this.storage = deps.storage === undefined ? defaultStorage() : deps.storage;
    this.load = deps.load ?? defaultLoadAuth;
  }

  get user(): AuthUser | null {
    return this.sig.get().user;
  }

  get signedIn(): boolean {
    return this.sig.get().phase === 'signedIn';
  }

  /** Signed in without a username (ask for one). */
  get needsUsername(): boolean {
    const u = this.user;
    return u !== null && u.username === null;
  }

  /**
   * `/api/info.auth` (null = no sign-in on this server). The first answer with a provider restores
   * an earlier session when the marker says there was one.
   */
  setInfo(info: AuthInfo | null): void {
    const prev = this.info;
    if (prev !== null && info !== null && prev.provider === info.provider) {
      if (prev.privyAppId === info.privyAppId) return;
    }
    if (prev === null && info === null) return;
    this.info = info;
    this.detach();
    this.module = null;
    this.loading = null;
    this.loadFailed = false;
    if (info === null) {
      this.publish();
      return;
    }
    this.publish();
    if (readKey(AUTH_MARKER_KEY, this.storage) === info.provider) void this.ensureModule();
  }

  /**
   * Opens the provider's sign-in (loading it first when needed). Resolves true once signed in
   * (the account was read), false when the player closed it or sign-in is unavailable.
   */
  async signIn(): Promise<boolean> {
    if (this.signedIn) return true;
    if (this.info === null) return false;
    this.busy(+1);
    try {
      const mod = await this.ensureModule();
      if (mod === null) return false;
      const ok = await mod.signIn();
      if (ok) writeKey(AUTH_MARKER_KEY, mod.provider, this.storage);
      return ok && this.signedIn;
    } catch (err) {
      this.warn('ball.io: sign-in failed', err);
      return false;
    } finally {
      this.busy(-1);
    }
  }

  async signOut(): Promise<void> {
    removeKey(AUTH_MARKER_KEY, this.storage);
    const mod = this.module;
    if (mod === null) {
      this.publish();
      return;
    }
    await mod.signOut().catch((err: unknown) => this.warn('ball.io: sign-out failed', err));
    this.publish();
  }

  /** A valid access token, or null while signed out. */
  token(opts?: { readonly fresh?: boolean }): Promise<string | null> {
    if (!this.signedIn || this.module === null) return Promise.resolve(null);
    return this.module.getAccessToken(opts).catch(() => null);
  }

  /**
   * Runs `call` with an access token. `refused(value)` says whether the server refused the token
   * (401 authInvalid / authRequired): then once more with a fresh token, and a second refusal signs
   * out.
   */
  async authed<T>(
    call: (token: string) => Promise<T>,
    refused: (value: T) => boolean,
  ): Promise<AuthedOutcome<T>> {
    const first = await this.token();
    if (first === null) return { status: 'noSession' };
    const value = await call(first);
    if (!refused(value)) return { status: 'done', value };
    const again = await this.token({ fresh: true });
    if (again !== null && again !== first) {
      const second = await call(again);
      if (!refused(second)) return { status: 'done', value: second };
    }
    await this.signOut();
    return { status: 'signedOut' };
  }

  /** POST /api/account/username, then re-reads the account (the menu shows the new name). */
  async setUsername(name: string): Promise<SetUsernameResult | null> {
    const out = await this.authed(
      (token) => postUsername(token, name, { baseUrl: this.deps.baseUrl }),
      (r) => r.status === 'error' && (r.error === 'authInvalid' || r.error === 'authRequired'),
    );
    if (out.status !== 'done') return null;
    if (out.value.status === 'ok' || out.value.error === 'alreadySet') {
      await this.module?.refreshAccount().catch(() => undefined);
    }
    this.publish();
    return out.value;
  }

  /** Re-reads GET /api/account/me (after a ban notice, or a username set elsewhere). */
  async refreshAccount(): Promise<void> {
    if (!this.signedIn || this.module === null) return;
    await this.module.refreshAccount().catch(() => undefined);
  }

  dispose(): void {
    this.detach();
  }

  // -------------------------------------------------------------------------------------------

  private ensureModule(): Promise<AuthModule | null> {
    if (this.module !== null) return Promise.resolve(this.module);
    if (this.loading !== null) return this.loading;
    const info = this.info;
    if (info === null) return Promise.resolve(null);
    this.busy(+1);
    const p = this.load(info, {
      baseUrl: this.deps.baseUrl,
      storage: this.storage,
      ...(this.deps.warn ? { warn: this.deps.warn } : {}),
    })
      .catch((err: unknown) => {
        this.warn('ball.io: sign-in unavailable', err);
        return null;
      })
      .then(async (mod) => {
        if (this.info !== info) return null;
        this.loading = null;
        if (mod === null) {
          this.loadFailed = true;
          return null;
        }
        this.module = mod;
        this.offModule = [
          mod.status.subscribe(() => this.publish()),
          mod.user.subscribe(() => this.publish()),
        ];
        if (mod.status.get() === 'signedIn') {
          writeKey(AUTH_MARKER_KEY, mod.provider, this.storage);
          await mod.refreshAccount().catch(() => undefined);
        }
        return mod;
      })
      .finally(() => this.busy(-1));
    this.loading = p;
    return p;
  }

  private detach(): void {
    for (const off of this.offModule.splice(0)) off();
  }

  private busy(delta: number): void {
    this.busyCount = Math.max(0, this.busyCount + delta);
    this.publish();
  }

  private publish(): void {
    const info = this.info;
    if (info === null) {
      this.sig.set(ACCOUNT_UNAVAILABLE);
      return;
    }
    const mod = this.module;
    const modStatus = mod?.status.get() ?? 'signedOut';
    const user = mod?.user.get() ?? null;
    let phase: AccountPhase;
    if (modStatus === 'signedIn' && user !== null) phase = 'signedIn';
    else if (this.busyCount > 0 || modStatus === 'loading') phase = 'busy';
    else phase = 'signedOut';
    // A session that ended by itself (expired, signed out in another tab) drops the marker.
    if (mod !== null && modStatus === 'signedOut' && this.busyCount === 0) {
      removeKey(AUTH_MARKER_KEY, this.storage);
    }
    this.sig.set({
      phase,
      provider: info.provider,
      user: phase === 'signedIn' ? user : null,
      loadFailed: this.loadFailed && phase === 'signedOut',
    });
  }

  private warn(msg: string, err?: unknown): void {
    (this.deps.warn ?? ((m, e) => console.warn(m, e)))(msg, err);
  }
}
